import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import LicenseForm from "../../components/LicenseForm";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import logo from "../../images/logo.png";

const DriverLicense = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [licenses, setLicenses] = useState([]);
  const [isSigned, setIsSigned] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch all licenses
  const fetchLicenses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/driverlicense/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const grouped = {};

      (res.data || []).forEach((row) => {
        if (!grouped[row.dl_id]) {
          grouped[row.dl_id] = {
            ...row,
            files: [],
            isNew: false,
          };
        }

        if (row.dlf_id) {
          grouped[row.dl_id].files.push({
            id: row.dlf_id,
            path: row.file_path,
            original_name: row.original_name,
            uploaded_at: row.uploaded_at,
          });
        }
      });

      const fetched = Object.values(grouped);

      // If no license exists → create a new PRIMARY license automatically
      if (fetched.length === 0) {
        setLicenses([
          {
            dl_status: "primary",
            dl_id: null,
            isNew: true,
          },
        ]);
      } else {
        setLicenses(fetched);
      }
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    if (userId) fetchLicenses();
  }, [fetchLicenses, userId]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 22; // 👈 match driver_license task id in DB
        const idKey = String(TASK_ID);
        const nameKey = "driver_license";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Driver's License
        </span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Driver's License</h2>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        {licenses.map((license, idx) => (
          <LicenseForm
            key={license.dl_id || idx}
            license={license}
            userId={userId}
            isAdmin={isAdmin}
            isSigned={isSigned}
            onSaved={fetchLicenses}
            onDeleted={fetchLicenses}
            onCancelNew={() =>
              setLicenses((prev) => prev.filter((l) => l !== license))
            }
          />
        ))}

        {(!isSigned || isAdmin) && (
          <button
            className="btn btn-success mt-3"
            onClick={() =>
              setLicenses([
                ...licenses,
                {
                  dl_status: licenses.length === 0 ? "primary" : "other",
                  dl_id: null,
                  isNew: true,
                },
              ])
            }
          >
            + Add License
          </button>
        )}

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="driver_license"
              taskId={22}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="driver_license"
              taskId={22}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverLicense;
